import { useCallback, useEffect, useRef } from 'react';
import { useChannelStateContext } from 'stream-chat-react';

import { AppAPIName } from 'src/constants';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { setStatus } from 'src/store/aiAssistantStatus/slice';
import { API } from 'src/utils/AmplifyApiUtils';

const TAKING_LONGER_TIMEOUT = 30000; // 30 seconds
const ERROR_TIMEOUT = 60000; // 60 seconds

export interface AIAssistantStatusData {
  status: string;
  metadata?: {
    tool?: string;
    query?: string;
    errorMessage?: string;
  };
  messageId?: string;
  isCancelable: boolean;
}

interface AIAssistantStatusHook {
  status: AIAssistantStatusData | null;
  cancelStream: () => Promise<void>;
}

export const useAIAssistantStatus = (): AIAssistantStatusHook => {
  const dispatch = useAppDispatch();
  const { channel } = useChannelStateContext();
  const channelId = channel?.cid;

  const statusFromStore = useAppSelector((state) =>
    channelId ? (state.aiAssistantStatus.statuses[channelId] ?? null) : null,
  );

  // Enhance status with isCancelable property
  const status = statusFromStore
    ? {
        ...statusFromStore,
        isCancelable: Boolean(
          statusFromStore &&
            statusFromStore.status !== 'error' &&
            statusFromStore.status !== 'canceling' &&
            statusFromStore.status !== 'canceled' &&
            statusFromStore.status &&
            statusFromStore.messageId,
        ),
      }
    : null;

  const takingLongerTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const errorTimeoutRef = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => {
    // Always clear existing timeouts first to prevent leaks
    if (takingLongerTimeoutRef.current) {
      clearTimeout(takingLongerTimeoutRef.current);
      takingLongerTimeoutRef.current = undefined;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = undefined;
    }

    // If backend sends an error status or no status, don't set any new timeouts
    if (!statusFromStore || !channelId || statusFromStore.status === 'error') {
      return;
    }

    // Capture current values to avoid race conditions
    const currentChannelId = channelId;
    const currentMetadata = statusFromStore.metadata;

    // Only set timeouts if we have an active status that's not already an error
    if (statusFromStore.status !== 'taking_longer') {
      // Set "taking longer" timeout
      takingLongerTimeoutRef.current = setTimeout(() => {
        dispatch(
          setStatus({
            channelId: currentChannelId,
            status: 'taking_longer',
            metadata: currentMetadata,
          }),
        );
      }, TAKING_LONGER_TIMEOUT);

      // Set error timeout
      errorTimeoutRef.current = setTimeout(() => {
        dispatch(
          setStatus({
            channelId: currentChannelId,
            status: 'error',
            metadata: {
              ...currentMetadata,
              errorMessage: 'Request timed out. Please try again.',
            },
          }),
        );
      }, ERROR_TIMEOUT);
    } else {
      // We're in taking_longer state, set a shorter error timeout
      errorTimeoutRef.current = setTimeout(() => {
        dispatch(
          setStatus({
            channelId: currentChannelId,
            status: 'error',
            metadata: {
              ...currentMetadata,
              errorMessage: 'Request timed out. Please try again.',
            },
          }),
        );
      }, ERROR_TIMEOUT - TAKING_LONGER_TIMEOUT); // Remaining time
    }

    // Cleanup on unmount or dependency change
    return () => {
      if (takingLongerTimeoutRef.current) {
        clearTimeout(takingLongerTimeoutRef.current);
        takingLongerTimeoutRef.current = undefined;
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = undefined;
      }
    };
  }, [statusFromStore, channelId, dispatch]);

  const cancelStream = useCallback(async () => {
    if (!channelId || !status?.messageId) {
      return;
    }

    // Immediately show "Canceling" status for user feedback
    dispatch(
      setStatus({
        channelId,
        status: 'canceling',
        messageId: status.messageId,
      }),
    );

    try {
      await API.post(AppAPIName, '/v1/ai/cancel', {
        body: {
          channelId,
          messageId: status.messageId,
        },
      });

      // The backend will send a WebSocket status update to show "canceled" then clear it
    } catch (error) {
      // On error, show error status
      dispatch(
        setStatus({
          channelId,
          status: 'error',
          metadata: { errorMessage: 'Failed to cancel' },
        }),
      );
    }
  }, [channelId, status?.messageId, dispatch]);

  return {
    status,
    cancelStream,
  };
};
